'use server'

import { apiRequest, ApiResponse } from '../request'

export interface HrHourBankConfig {
    id?: number
    companyId: number
    enabled: boolean
    compensationMonths: number
    maxPositiveBalance: number | null
    maxNegativeBalance: number | null
}

export interface HrHourBankEntry {
    id: number
    hourBankId: number
    type: 'CREDIT' | 'DEBIT'
    minutes: number
    date: string
    description: string | null
    timeSheetId: number | null
    expiresAt: string | null
    expired: boolean
    createdById: number | null
    createdBy?: { id: number; name: string } | null
    createdAt: string
    runningBalance?: number
}

export interface HrHourBank {
    id: number
    employeeId: number
    balanceMinutes: number
    employee?: { id: number; name: string; department: string | null }
    entries?: HrHourBankEntry[]
    createdAt: string
    updatedAt: string
}

export interface HrHourBankBalance {
    items: HrHourBank[]
    total: number
    page: number
    limit: number
}

export interface HrHourBankStatement {
    employee: { id: number; name: string; department: string | null }
    currentBalance: number
    initialBalance: number
    entries: HrHourBankEntry[]
}

const BASE = 'hr/hour-bank'

export async function getConfig(): Promise<ApiResponse<HrHourBankConfig>> {
    return apiRequest<HrHourBankConfig>(`${BASE}/config`, {})
}

export async function updateConfig(data: {
    enabled: boolean
    compensationMonths: number
    maxPositiveBalance?: number | null
    maxNegativeBalance?: number | null
}): Promise<ApiResponse<HrHourBankConfig>> {
    return apiRequest<HrHourBankConfig>(`${BASE}/config`, { data }, 'patch')
}

export async function getBalances(params?: {
    search?: string
    page?: number
    limit?: number
}): Promise<ApiResponse<HrHourBankBalance>> {
    return apiRequest<HrHourBankBalance>(`${BASE}/balances`, { params })
}

export async function getBalance(employeeId: number): Promise<ApiResponse<HrHourBank>> {
    return apiRequest<HrHourBank>(`${BASE}/balance/${employeeId}`, {})
}

export async function addEntry(data: {
    employeeId: number
    type: 'CREDIT' | 'DEBIT'
    minutes: number
    date: string
    description?: string
}): Promise<ApiResponse<HrHourBankEntry>> {
    return apiRequest<HrHourBankEntry>(`${BASE}/entries`, { data }, 'post')
}

export async function getEntries(
    employeeId: number,
    params?: { startDate?: string; endDate?: string; type?: string },
): Promise<ApiResponse<HrHourBankEntry[]>> {
    return apiRequest<HrHourBankEntry[]>(`${BASE}/entries/${employeeId}`, { params })
}

export async function getStatement(
    employeeId: number,
    startDate: string,
    endDate: string,
): Promise<ApiResponse<HrHourBankStatement>> {
    return apiRequest<HrHourBankStatement>(`${BASE}/statement/${employeeId}`, {
        params: { startDate, endDate },
    })
}

export async function expireEntries(): Promise<ApiResponse<{ count: number; entries: HrHourBankEntry[] }>> {
    return apiRequest<{ count: number; entries: HrHourBankEntry[] }>(`${BASE}/expire`, {}, 'post')
}
